import React, { useEffect, useMemo, useState } from "react";
import { api, apiError } from "../api/client";
import { useI18n, formatDateTime, formatMoney } from "../i18n";
import { useAuth } from "../contexts/AuthContext";
import { PageHeader, Card, Badge, EmptyState } from "../components/shared";
import {
  PiggyBank, DoorOpen, DoorClosed, Banknote, Smartphone, CreditCard,
  Wallet, User, UserCog, Clock, X, CheckCircle2, AlertTriangle, TrendingUp, History,
} from "lucide-react";
import { toast } from "sonner";

const METHOD_META = {
  cash: { labelKey: "payments.cash", icon: Banknote, cls: "bg-[#2d8a56]/10 text-[#2d8a56]" },
  card: { labelKey: "payments.card", icon: CreditCard, cls: "bg-[#2563eb]/10 text-[#2563eb]" },
  wave: { labelKey: "payments.wave", icon: Smartphone, cls: "bg-[#2563eb]/10 text-[#2563eb]" },
  orange_money: { labelKey: "payments.orange_money", icon: Smartphone, cls: "bg-[#e3a03b]/15 text-[#8a5a10]" },
};

const INPUT =
  "w-full rounded-xl border border-[var(--border-default)] bg-white px-4 h-12 text-base focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none";

export default function Cash() {
  const { t } = useI18n();
  const { tenant } = useAuth();
  const cur = tenant?.currency || "XOF";
  const [session, setSession] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [closeModal, setCloseModal] = useState(false);
  const [historyModal, setHistoryModal] = useState(null);
  const [openAmt, setOpenAmt] = useState("");
  const [closeAmt, setCloseAmt] = useState("");
  const [closeNotes, setCloseNotes] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [c, h] = await Promise.all([
        api.get("/cash/current"),
        api.get("/cash/history"),
      ]);
      setSession(c.data || null);
      setHistory(h.data || []);
    } catch (e) {
      toast.error(apiError(e, t("common.error")));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCash = async (e) => {
    e?.preventDefault();
    try {
      await api.post("/cash/open", { opening_balance: parseFloat(openAmt) || 0 });
      toast.success(t("common.success"));
      setOpenAmt("");
      setOpenModal(false);
      load();
    } catch (err) { toast.error(apiError(err, t("common.error"))); }
  };

  const closeCash = async (e) => {
    e?.preventDefault();
    try {
      const { data } = await api.post("/cash/close", {
        closing_balance: parseFloat(closeAmt) || 0,
        notes: closeNotes,
      });
      const v = data.variance || 0;
      if (v === 0) toast.success(t("cash.varianceOk"));
      else if (v > 0) toast.warning(`${t("cash.varianceOver")}: +${formatMoney(v, cur)}`);
      else toast.error(`${t("cash.varianceShort")}: ${formatMoney(v, cur)}`);
      setCloseAmt("");
      setCloseNotes("");
      setCloseModal(false);
      load();
    } catch (err) { toast.error(apiError(err, t("common.error"))); }
  };

  // ---------- Render ----------
  const isOpen = !!session;
  const totals = session?.totals || { by_method: {}, count_by_method: {}, total_collected: 0, count: 0 };
  const openingBalance = session?.opening_balance || 0;
  const cashCollected = totals.by_method?.cash || 0;
  const expected = session?.expected_balance ?? (openingBalance + cashCollected);

  return (
    <div>
      <PageHeader
        title={t("cash.title")}
        actions={
          isOpen ? (
            <button
              onClick={() => { setCloseAmt(String(Math.round(expected))); setCloseModal(true); }}
              data-testid="cash-close-btn"
              className="h-12 px-5 rounded-xl bg-terracotta hover:bg-terracotta-hover text-white font-heading font-bold flex items-center gap-2"
            >
              <DoorClosed className="w-5 h-5" /> {t("cash.close")}
            </button>
          ) : (
            <button
              onClick={() => setOpenModal(true)}
              data-testid="cash-open-btn"
              className="h-12 px-5 rounded-xl bg-brand hover:bg-brand-hover text-white font-heading font-bold flex items-center gap-2"
            >
              <DoorOpen className="w-5 h-5" /> {t("cash.open")}
            </button>
          )
        }
      />

      {loading ? (
        <Card className="py-16 text-center text-ink-muted">{t("common.loading")}</Card>
      ) : !isOpen ? (
        <NoSession t={t} />
      ) : (
        <ActiveSession
          session={session}
          totals={totals}
          openingBalance={openingBalance}
          expected={expected}
          cur={cur}
          t={t}
        />
      )}

      {/* History section */}
      <div className="mt-8">
        <div className="flex items-center gap-2 mb-4">
          <History className="w-5 h-5 text-brand" />
          <h2 className="font-heading font-bold text-ink text-xl">{t("cash.history")}</h2>
        </div>
        <Card className="overflow-hidden" data-testid="cash-history">
          {history.length === 0 ? (
            <EmptyState icon={PiggyBank} title={t("common.noData")} />
          ) : (
            <>
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-subtle text-xs uppercase tracking-wider text-ink-muted">
                      <th className="text-left py-3 px-5 font-semibold">{t("common.date")}</th>
                      <th className="text-left py-3 px-5 font-semibold">{t("cash.openedBy")}</th>
                      <th className="text-left py-3 px-5 font-semibold">{t("cash.closedBy")}</th>
                      <th className="text-right py-3 px-5 font-semibold">{t("cash.opening")}</th>
                      <th className="text-right py-3 px-5 font-semibold">{t("cash.closing")}</th>
                      <th className="text-right py-3 px-5 font-semibold">{t("cash.variance")}</th>
                      <th className="text-center py-3 px-5 font-semibold">{t("common.status")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((s) => (
                      <HistoryRow key={s.id} s={s} cur={cur} t={t} onClick={() => setHistoryModal(s)} />
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="md:hidden divide-y divide-[var(--border-default)]">
                {history.map((s) => (
                  <HistoryMobileCard key={s.id} s={s} cur={cur} t={t} onClick={() => setHistoryModal(s)} />
                ))}
              </div>
            </>
          )}
        </Card>
      </div>

      {/* Open cash modal */}
      {openModal && (
        <Modal onClose={() => setOpenModal(false)} title={t("cash.open")} testid="open-modal">
          <form onSubmit={openCash} className="space-y-4">
            <div>
              <label className="block mb-2 text-sm font-semibold">{t("cash.opening")}</label>
              <input type="number" min={0} required className={INPUT}
                placeholder="0" value={openAmt}
                onChange={(e) => setOpenAmt(e.target.value)}
                data-testid="cash-open-amt" autoFocus />
              <div className="text-xs text-ink-muted mt-1">{t("cash.opening")} (en {cur})</div>
            </div>
            <button type="submit" data-testid="cash-open-submit"
              className="w-full h-12 rounded-xl bg-brand hover:bg-brand-hover text-white font-heading font-bold flex items-center justify-center gap-2">
              <DoorOpen className="w-5 h-5" /> {t("cash.open")}
            </button>
          </form>
        </Modal>
      )}

      {/* Close cash modal */}
      {closeModal && (
        <CloseCashModal
          onClose={() => setCloseModal(false)}
          onSubmit={closeCash}
          closeAmt={closeAmt}
          setCloseAmt={setCloseAmt}
          closeNotes={closeNotes}
          setCloseNotes={setCloseNotes}
          expected={expected}
          openingBalance={openingBalance}
          cashCollected={cashCollected}
          cur={cur}
          t={t}
        />
      )}

      {/* History session detail */}
      {historyModal && (
        <HistoryDetailModal s={historyModal} cur={cur} t={t} onClose={() => setHistoryModal(null)} />
      )}
    </div>
  );
}

// ---------- Sub-components ----------

function NoSession({ t }) {
  return (
    <Card className="py-16 text-center" data-testid="cash-no-session">
      <div className="flex flex-col items-center gap-3">
        <div className="w-16 h-16 rounded-2xl bg-brand/10 flex items-center justify-center">
          <PiggyBank className="w-8 h-8 text-brand" />
        </div>
        <h3 className="font-heading font-bold text-ink text-xl">{t("cash.noSession")}</h3>
        <p className="text-sm text-ink-muted max-w-md">
          Ouvrez une session pour commencer à suivre les encaissements du jour.
        </p>
      </div>
    </Card>
  );
}

function ActiveSession({ session, totals, openingBalance, expected, cur, t }) {
  const opener = session.opener || {};
  const openedAt = session.opened_at;
  const duration = useMemo(() => {
    if (!openedAt) return "";
    const start = new Date(openedAt);
    const mins = Math.floor((Date.now() - start.getTime()) / 60000);
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h > 0 ? `${h}h ${m}min` : `${m}min`;
  }, [openedAt]);

  return (
    <div data-testid="cash-active-session" className="space-y-6">
      {/* Hero */}
      <Card className="p-6 bg-gradient-to-br from-brand to-[#065841] text-white border-0 shadow-[0_4px_16px_-4px_rgba(8,105,80,0.2)]">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center">
              <PiggyBank className="w-6 h-6" />
            </div>
            <div>
              <div className="text-xs uppercase opacity-80 tracking-wider">{t("cash.session")}</div>
              <div className="font-heading font-extrabold text-xl">{t("cash.statusOpen")}</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
            <HeroField icon={User} label={t("cash.openedBy")} value={opener.first_name ? `${opener.first_name} ${opener.last_name}` : "—"} />
            <HeroField icon={Clock} label={t("cash.openedAt")} value={formatDateTime(openedAt)} />
            <HeroField icon={Clock} label={t("cash.duration")} value={duration} />
          </div>
        </div>
      </Card>

      {/* Key numbers */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          testid="kpi-opening"
          icon={DoorOpen}
          label={t("cash.opening")}
          value={formatMoney(openingBalance, cur)}
          tone="neutral"
        />
        <KpiCard
          testid="kpi-collected"
          icon={TrendingUp}
          label={t("cash.totalCollected")}
          value={formatMoney(totals.total_collected || 0, cur)}
          hint={`${totals.count || 0} op.`}
          tone="success"
        />
        <KpiCard
          testid="kpi-cash-in"
          icon={Banknote}
          label={t("cash.cashIn")}
          value={formatMoney(totals.by_method?.cash || 0, cur)}
          hint={`${totals.count_by_method?.cash || 0} op.`}
          tone="brand"
        />
        <KpiCard
          testid="kpi-expected"
          icon={Wallet}
          label={t("cash.expected")}
          value={formatMoney(expected, cur)}
          hint={t("cash.cashExpected")}
          tone="warning"
        />
      </div>

      {/* Breakdown by method */}
      <Card className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <Wallet className="w-5 h-5 text-brand" />
          <h3 className="font-heading font-bold text-ink">{t("cash.byMethod")}</h3>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="cash-by-method">
          {["cash", "wave", "orange_money", "card"].map((m) => (
            <MethodTotal key={m} method={m} amount={totals.by_method?.[m] || 0}
              count={totals.count_by_method?.[m] || 0} cur={cur} t={t} testid={`by-${m}`} />
          ))}
        </div>
      </Card>

      {/* Transactions */}
      <Card className="overflow-hidden" data-testid="cash-transactions">
        <div className="flex items-center gap-2 p-5 border-b border-[var(--border-default)]">
          <TrendingUp className="w-5 h-5 text-brand" />
          <h3 className="font-heading font-bold text-ink">{t("cash.transactions")}</h3>
          <Badge tone="brand" className="ml-auto">{(session.transactions || []).length}</Badge>
        </div>
        {(session.transactions || []).length === 0 ? (
          <EmptyState icon={Wallet} title={t("cash.noTransactions")} />
        ) : (
          <div className="divide-y divide-[var(--border-default)]">
            {session.transactions.map((p) => (
              <TxRow key={p.id} p={p} cur={cur} t={t} />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function HeroField({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4 opacity-80" />
      <div>
        <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
        <div className="font-semibold">{value}</div>
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, hint, tone, testid }) {
  const tones = {
    neutral: "bg-subtle text-ink",
    brand: "bg-brand/10 text-brand",
    success: "bg-[#2d8a56]/10 text-[#2d8a56]",
    warning: "bg-[#e3a03b]/15 text-[#8a5a10]",
    danger: "bg-[#d34044]/10 text-[#b12e31]",
  };
  return (
    <Card className="p-4 flex items-start gap-3" data-testid={testid}>
      <div className={`p-2.5 rounded-xl ${tones[tone] || tones.neutral}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs text-ink-muted uppercase tracking-wider">{label}</div>
        <div className="stat-number text-lg font-heading font-bold text-ink truncate">{value}</div>
        {hint && <div className="text-xs text-ink-muted">{hint}</div>}
      </div>
    </Card>
  );
}

function MethodTotal({ method, amount, count, cur, t, testid }) {
  const meta = METHOD_META[method];
  const Icon = meta.icon;
  return (
    <div className="p-3 rounded-xl border border-[var(--border-default)] flex items-start gap-3" data-testid={testid}>
      <div className={`p-2 rounded-lg ${meta.cls}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-xs text-ink-muted uppercase tracking-wider">{t(meta.labelKey)}</div>
        <div className="stat-number font-heading font-bold text-ink truncate">{formatMoney(amount, cur)}</div>
        <div className="text-xs text-ink-muted">{count} op.</div>
      </div>
    </div>
  );
}

function TxRow({ p, cur, t }) {
  const patient = p.patient || {};
  const meta = METHOD_META[p.method] || METHOD_META.cash;
  const Icon = meta.icon;
  const dt = p.created_at ? new Date(p.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "—";
  return (
    <div className="p-4 flex items-center gap-3" data-testid={`tx-${p.id}`}>
      <div className={`p-2 rounded-lg ${meta.cls} shrink-0`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-xs text-ink-muted shrink-0">{dt}</span>
          <span className="font-semibold text-ink truncate">
            {patient.first_name ? `${patient.first_name} ${patient.last_name}` : "—"}
          </span>
          {p.invoice_number && <span className="text-xs font-mono text-ink-muted">· {p.invoice_number}</span>}
        </div>
        <div className="text-xs text-ink-muted">{t(meta.labelKey)}</div>
      </div>
      <div className="stat-number font-bold text-ink shrink-0">{formatMoney(p.amount, cur)}</div>
    </div>
  );
}

function HistoryRow({ s, cur, t, onClick }) {
  const opener = s.opener || {};
  const closer = s.closer || {};
  const v = s.variance ?? null;
  return (
    <tr className="border-t border-[var(--border-default)] hover:bg-subtle/50 cursor-pointer"
      onClick={onClick} data-testid={`hist-${s.id}`}>
      <td className="py-3 px-5">
        <div className="text-sm text-ink">{formatDateTime(s.opened_at)}</div>
        {s.closed_at && <div className="text-xs text-ink-muted">→ {formatDateTime(s.closed_at)}</div>}
      </td>
      <td className="py-3 px-5 text-sm">{opener.first_name ? `${opener.first_name} ${opener.last_name}` : "—"}</td>
      <td className="py-3 px-5 text-sm">{closer.first_name ? `${closer.first_name} ${closer.last_name}` : "—"}</td>
      <td className="py-3 px-5 text-right stat-number">{formatMoney(s.opening_balance || 0, cur)}</td>
      <td className="py-3 px-5 text-right stat-number">{s.closing_balance != null ? formatMoney(s.closing_balance, cur) : "—"}</td>
      <td className="py-3 px-5 text-right">
        {v == null ? "—" : <VarianceBadge v={v} cur={cur} t={t} />}
      </td>
      <td className="py-3 px-5 text-center">
        <Badge tone={s.status === "open" ? "success" : "neutral"}>
          {s.status === "open" ? t("cash.statusOpen") : t("cash.statusClosed")}
        </Badge>
      </td>
    </tr>
  );
}

function HistoryMobileCard({ s, cur, t, onClick }) {
  const opener = s.opener || {};
  return (
    <button onClick={onClick} data-testid={`hist-${s.id}`}
      className="w-full text-left p-4 hover:bg-subtle/50">
      <div className="flex items-center justify-between mb-1">
        <div className="text-sm font-semibold text-ink">{formatDateTime(s.opened_at)}</div>
        <Badge tone={s.status === "open" ? "success" : "neutral"}>
          {s.status === "open" ? t("cash.statusOpen") : t("cash.statusClosed")}
        </Badge>
      </div>
      <div className="text-xs text-ink-muted mb-2">{opener.first_name ? `${opener.first_name} ${opener.last_name}` : "—"}</div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-ink-muted">{t("cash.opening")}: <span className="stat-number text-ink font-semibold">{formatMoney(s.opening_balance || 0, cur)}</span></span>
        {s.variance != null && <VarianceBadge v={s.variance} cur={cur} t={t} />}
      </div>
    </button>
  );
}

function VarianceBadge({ v, cur, t }) {
  if (v === 0) return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[#2d8a56]/10 text-[#2d8a56]"><CheckCircle2 className="w-3 h-3" /> {formatMoney(0, cur)}</span>;
  if (v > 0) return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[#e3a03b]/15 text-[#8a5a10]"><AlertTriangle className="w-3 h-3" /> +{formatMoney(v, cur)}</span>;
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-[#d34044]/10 text-[#b12e31]"><AlertTriangle className="w-3 h-3" /> {formatMoney(v, cur)}</span>;
}

function Modal({ children, title, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6 bg-black/50" data-testid={testid}>
      <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-[var(--border-default)]">
          <h3 className="font-heading font-bold text-xl">{title}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-subtle"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function CloseCashModal({ onClose, onSubmit, closeAmt, setCloseAmt, closeNotes, setCloseNotes,
                         expected, openingBalance, cashCollected, cur, t }) {
  const actual = parseFloat(closeAmt) || 0;
  const variance = actual - expected;
  return (
    <Modal onClose={onClose} title={t("cash.close")} testid="close-modal">
      <div className="space-y-4">
        {/* Expected summary */}
        <div className="p-4 rounded-xl bg-subtle space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-ink-muted">{t("cash.opening")}</span>
            <span className="stat-number font-semibold">{formatMoney(openingBalance, cur)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-ink-muted">+ {t("cash.cashIn")}</span>
            <span className="stat-number font-semibold">{formatMoney(cashCollected, cur)}</span>
          </div>
          <div className="h-px bg-[var(--border-default)]" />
          <div className="flex justify-between text-base">
            <span className="font-semibold text-ink">= {t("cash.expected")}</span>
            <span className="stat-number font-heading font-extrabold text-brand">{formatMoney(expected, cur)}</span>
          </div>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block mb-2 text-sm font-semibold">{t("cash.actualCounted")}</label>
            <input type="number" min={0} step="0.01" required className={INPUT}
              value={closeAmt} onChange={(e) => setCloseAmt(e.target.value)}
              data-testid="cash-close-amt" autoFocus />
            <div className="text-xs text-ink-muted mt-1">{t("cash.countedHint")}</div>
          </div>

          {/* Live variance */}
          <div className={`p-4 rounded-xl ${variance === 0 ? "bg-[#2d8a56]/10" : variance > 0 ? "bg-[#e3a03b]/15" : "bg-[#d34044]/10"}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {variance === 0 ? <CheckCircle2 className="w-5 h-5 text-[#2d8a56]" /> :
                 <AlertTriangle className={`w-5 h-5 ${variance > 0 ? "text-[#8a5a10]" : "text-[#b12e31]"}`} />}
                <div>
                  <div className="text-xs uppercase tracking-wider text-ink-muted">{t("cash.variance")}</div>
                  <div className="font-semibold">
                    {variance === 0 ? t("cash.varianceOk") :
                     variance > 0 ? t("cash.varianceOver") : t("cash.varianceShort")}
                  </div>
                </div>
              </div>
              <div className={`stat-number text-xl font-heading font-extrabold ${variance === 0 ? "text-[#2d8a56]" : variance > 0 ? "text-[#8a5a10]" : "text-[#b12e31]"}`}>
                {variance > 0 ? "+" : ""}{formatMoney(variance, cur)}
              </div>
            </div>
          </div>

          <div>
            <label className="block mb-2 text-sm font-semibold">Notes (optionnel)</label>
            <textarea className={`${INPUT} h-20 py-2`} value={closeNotes}
              onChange={(e) => setCloseNotes(e.target.value)}
              placeholder="Explication si écart..." />
          </div>

          <button type="submit" data-testid="cash-close-submit"
            className="w-full h-12 rounded-xl bg-terracotta hover:bg-terracotta-hover text-white font-heading font-bold flex items-center justify-center gap-2">
            <DoorClosed className="w-5 h-5" /> {t("cash.confirmClose")}
          </button>
        </form>
      </div>
    </Modal>
  );
}

function HistoryDetailModal({ s, cur, t, onClose }) {
  const opener = s.opener || {};
  const closer = s.closer || {};
  const byMethod = s.totals_by_method || {};
  const countMethod = s.count_by_method || {};
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6 bg-black/50" data-testid="hist-detail-modal">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-[var(--border-default)] sticky top-0 bg-white z-10">
          <div>
            <div className="text-xs text-ink-muted uppercase tracking-wider">{t("cash.session")}</div>
            <h3 className="font-heading font-bold text-xl">{formatDateTime(s.opened_at)}</h3>
          </div>
          <button onClick={onClose} data-testid="hist-detail-close" className="p-2 rounded-lg hover:bg-subtle"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-xl bg-subtle">
              <div className="flex items-center gap-2 mb-1"><User className="w-4 h-4 text-brand" /><div className="text-xs uppercase text-ink-muted">{t("cash.openedBy")}</div></div>
              <div className="font-semibold">{opener.first_name ? `${opener.first_name} ${opener.last_name}` : "—"}</div>
              <div className="text-xs text-ink-muted mt-1">{formatDateTime(s.opened_at)}</div>
            </div>
            <div className="p-3 rounded-xl bg-subtle">
              <div className="flex items-center gap-2 mb-1"><UserCog className="w-4 h-4 text-brand" /><div className="text-xs uppercase text-ink-muted">{t("cash.closedBy")}</div></div>
              <div className="font-semibold">{closer.first_name ? `${closer.first_name} ${closer.last_name}` : "—"}</div>
              <div className="text-xs text-ink-muted mt-1">{s.closed_at ? formatDateTime(s.closed_at) : "—"}</div>
            </div>
          </div>

          {s.status === "closed" && (
            <>
              <div className="p-4 rounded-xl bg-subtle space-y-1.5">
                <Row k={t("cash.opening")} v={formatMoney(s.opening_balance || 0, cur)} />
                <Row k={t("cash.cashIn")} v={formatMoney(s.total_cash_in || 0, cur)} />
                <Row k={t("cash.expected")} v={formatMoney(s.expected_balance || 0, cur)} strong />
                <Row k={t("cash.closing")} v={formatMoney(s.closing_balance || 0, cur)} strong />
                <div className="h-px bg-[var(--border-default)] my-1" />
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-ink">{t("cash.variance")}</span>
                  <VarianceBadge v={s.variance || 0} cur={cur} t={t} />
                </div>
              </div>

              {Object.keys(byMethod).length > 0 && (
                <div>
                  <div className="text-sm font-semibold text-ink mb-2">{t("cash.byMethod")}</div>
                  <div className="grid grid-cols-2 gap-3">
                    {["cash", "wave", "orange_money", "card"].map((m) => (
                      <MethodTotal key={m} method={m}
                        amount={byMethod[m] || 0}
                        count={countMethod[m] || 0}
                        cur={cur} t={t} testid={`hist-by-${m}`} />
                    ))}
                  </div>
                </div>
              )}

              {s.notes && (
                <div className="p-3 rounded-xl border border-[var(--border-default)]">
                  <div className="text-xs text-ink-muted uppercase mb-1">Notes</div>
                  <div className="text-sm">{s.notes}</div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ k, v, strong }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`text-sm ${strong ? "font-semibold text-ink" : "text-ink-muted"}`}>{k}</span>
      <span className={`stat-number ${strong ? "font-bold text-base text-ink" : "text-sm text-ink"}`}>{v}</span>
    </div>
  );
}
